import React from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import { TableOfContentsBlock } from '../../../_blocks/TableOfContents/TableOfContentsBlock';
import { AddContentBlock } from './partials/AddContentBlock/AddContentBlock';
import { useContentWrapper } from './useContentWrapper';
import { nextBlockIsRichtext } from './ContentWrapper.utils';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';
import { ContentWrapperContextProvider } from './ContentWrapperContext';
import { CollapsibleExpander } from '../../../_lib/components/CollapsibleExpander';
import { Namespace } from '../../../translation/namespace';
import { DragAndDropProvider } from '../../../_lib/DragAndDrop';
import { ContentWrapperDndProvider } from './ContentWrapperDndProvider';
import { BlockType } from '../../../types/api/ElementType';
import { LayoutGroup } from '../LayoutGroup/LayoutGroup';
import { VideoBlock } from '../../../_blocks/VideoBlock';
import { ContentImageBlock } from '../../../_blocks/ContentImage/ContentImageBlock';
import { RichTextBlock } from '../../../_blocks/RichText';
import { GenericSearchListBlock } from '../../../_blocks/GenericSearchList';
import { ChildPagesBlock } from '../../../_blocks/ChildPages/ChildPagesBlock';
import { PersonCollectionBlock } from '../../../_blocks/PersonCollection/PersonCollectionBlock';
import { ImageGalleryBlock } from '../../../_blocks/ImageGallery/ImageGallery';
import { ContentWrapperBlockProps } from './ContentWrapper.types';
import { TicketBookingBlock } from '../../../_blocks/TicketBooking/TicketBookingBlock';
import { isDynamicallyGeneratedBlockContent } from '../../../types/api/Block';
import { DynamicallyGeneratedContentHint } from '../../../_lib/components/Hint';
import { ExternalContentBlock } from '../../../_blocks/ExternalContent';
import { SkipLink } from '../../../_lib/components/SkipLink';

// checking if a single block has edit view
const blocksContainEditView = (blocks: any) => blocks.some((block: any) => block.embedded?.views?.edit);

export function ContentWrapper(props: ContentWrapperBlockProps & { fetchUrl: string }) {
  const { t } = useTranslation(Namespace.ContentWrapper);
  const { fetchUrl, views } = props;
  const addBlockView = views.addBlock;
  const tableOfContents = views.default.embedded?.tableOfContents;

  const {
    blocks,
    addBlock,
    error,
    setError,
    refetch,
    action,
    setAction,
    onDeleteSuccess,
    onMovingFailure,
    onMovingSuccess,
    moveBlockToBlock,
    submitNewBlockToBlockMove,
    createSnapshotOfBlockOrder,
  } = useContentWrapper(views.default.embedded.content, fetchUrl);

  const numberOfCollapsibleBlocks = blocks.filter(
    block =>
      block.elementType === 'richtext' &&
      block.fetchType === 'instant' &&
      block.embedded?.views?.default?.embedded?.collapsible,
  )?.length;

  const getContentWrapperBlocks = (elementType: BlockType, block: any) => {
    if (isDynamicallyGeneratedBlockContent(block)) {
      let customHelpText;
      if (elementType === BlockType.ticketbooking) {
        customHelpText = t(`${Namespace.TicketBooking}:dynamicallyGeneratedBlockContent.explanation`);
      }
      return <DynamicallyGeneratedContentHint key={block.uuid} customHelpText={customHelpText} />;
    }

    switch (elementType) {
      case BlockType.video:
        return <VideoBlock key={block.uuid} {...block} />;
      case BlockType.contentimage:
        return <ContentImageBlock key={block.uuid} {...block} />;
      case BlockType.richtext:
        return <RichTextBlock key={block.uuid} {...block} />;
      case BlockType.genericsearchlist:
        return <GenericSearchListBlock key={block.uuid} {...block} />;
      case BlockType.childpages:
        return <ChildPagesBlock key={block.uuid} {...block} />;
      case BlockType.personcollection:
        return <PersonCollectionBlock key={block.uuid} {...block} />;
      case BlockType.imagegallery:
        return <ImageGalleryBlock key={block.uuid} {...block} />;
      case BlockType.layoutgroup:
        return <LayoutGroup key={block.uuid} {...block} />;
      case BlockType.ticketbooking:
        return <TicketBookingBlock key={block.uuid} {...block} />;
      case BlockType.externalcontent:
        return <ExternalContentBlock key={block.uuid} {...block} />;
      default:
        return null;
    }
  };

  return (
    <>
      {blocksContainEditView(blocks) && blocks.length > 2 && (
        <div className="content-wrapper__skip-link">
          <SkipLink id={GlobalDocumentIds.addContentBlock}>{t('skipLink') || ''}</SkipLink>
        </div>
      )}

      <CollapsibleExpander showExpander={numberOfCollapsibleBlocks > 1}>
        {isNotNullOrUndefined(tableOfContents) && <TableOfContentsBlock {...tableOfContents} />}
        <div className="content-wrapper">
          <DragAndDropProvider>
            {blocks.map((block, index) => (
              <ContentWrapperContextProvider
                key={block.uuid}
                block={block}
                index={index}
                error={error}
                action={action}
                numBlocks={blocks.length}
                setAction={setAction}
                addBlock={addBlock}
                setError={setError}
                nextBlockIsRichtext={nextBlockIsRichtext(blocks, index)}
                onDeleteSuccess={onDeleteSuccess}
                onMovingFailure={onMovingFailure}
                onMovingSuccess={onMovingSuccess}
                refetch={refetch}
              >
                <ContentWrapperDndProvider
                  block={block}
                  position={index + 1}
                  moveBlockToBlock={moveBlockToBlock}
                  submitNewBlockToBlockMove={submitNewBlockToBlockMove}
                  createSnapshotOfBlockOrder={createSnapshotOfBlockOrder}
                >
                  {getContentWrapperBlocks(block.elementType, block)}
                </ContentWrapperDndProvider>
              </ContentWrapperContextProvider>
            ))}
          </DragAndDropProvider>
        </div>
      </CollapsibleExpander>
      {isNotNullOrUndefined(addBlockView) && (
        <AddContentBlock
          form={addBlockView.embedded.form}
          addBlock={addBlock}
          action={action}
          setAction={setAction}
          setError={setError}
          error={error}
          position={blocks.length}
        />
      )}
    </>
  );
}
